<template>
  <!-- The card that turns (mystic room 2026-10-30, DESIGN.md §3). The
       portrait is a card: tapping it turns it over to the short About. The
       turn is 0.6s; the size change is packed into its middle, when the card
       is nearly edge-on, so it reads as one move. The portrait is the only
       way in (Chris's final call): no "About {name} ›" beside the button.
       The back is the whole About (Chris 2026-10-30, no second page): the
       name, the full description, the rows, the signs, then Card Deck ›,
       Lessons › (and Today's drop ›) opening in place, with a back arrow;
       long content scrolls inside the card's frame. -->
  <div
    class="room-card"
    :class="{ 'is-flipped': flipped, 'is-turning': turning, 'is-wide': wide, 'is-arrive': scene === 'arrive', 'is-started': started, 'is-cue': cue, 'is-reduced': reduced }"
    :style="boxStyle"
  >
    <div class="rc-lift">
      <div class="rc-tilt">
        <div class="rc-card">
          <div
            ref="frontEl"
            class="rc-face rc-front"
            data-testid="room-card-front"
            role="button"
            :tabindex="flipped ? -1 : 0"
            :aria-label="`About ${name}, turn the card`"
            :aria-expanded="flipped && !dayShown ? 'true' : 'false'"
            @click="$emit('toggle')"
            @keydown.enter.prevent="$emit('toggle')"
            @keydown.space.prevent="$emit('toggle')"
          >
            <img v-if="image" :src="image" :alt="name" class="rc-img" @load="$emit('image-ready')" @error="$emit('image-ready')" />
            <div v-else class="rc-initial" aria-hidden="true">{{ initial }}</div>
            <span class="rc-glint" aria-hidden="true"></span>
            <span class="rc-turnmark" aria-hidden="true">↻</span>
          </div>

          <div
            class="rc-face rc-back"
            data-testid="room-card-back"
            role="region"
            :aria-label="dayShown ? 'About this day' : `About ${name}`"
            :aria-hidden="flipped ? 'false' : 'true'"
            :inert="!flipped"
          >
            <div class="rc-frame" aria-hidden="true"><b>✦</b><b>✦</b><b>✦</b><b>✦</b></div>
            <svg class="rc-sigil" viewBox="0 0 100 100" aria-hidden="true"><g fill="none" stroke="#f0d9a3" stroke-width=".8"><circle cx="50" cy="50" r="46"/><circle cx="50" cy="50" r="34"/><path d="M50 4v92M4 50h92M17 17l66 66M83 17L17 83"/><path d="M50 16l9 25 25 9-25 9-9 25-9-25-25-9 25-9z"/></g></svg>
            <button type="button" class="rc-back-x" data-testid="room-card-unflip" aria-label="Turn the card back" @click.stop="$emit('unflip')">
              <svg width="13" height="13" viewBox="0 0 14 14" aria-hidden="true"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
            </button>
            <button
              v-if="view !== 'summary' && !dayShown"
              type="button" class="rc-back-x rc-panel-back" data-testid="room-card-panel-back"
              :aria-label="`Back to ${name}`" @click.stop="closePanel"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M9 2L4 7l5 5" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <div ref="innerEl" class="rc-inner" :class="{ 'is-panel': view !== 'summary' && !dayShown, 'is-day': dayShown, 'is-day-both': dayShown && dayAlso.length }">
              <!-- The day's face (holiday line 2026-10-30, DESIGN.md §4): the
                   same tarot back, turned to by the sky line. What kind of
                   day, a small drawn glyph, the name, how to say it, when,
                   three one-sentence rows and a quiet note. No link out and
                   no reading button: Get Reading stays below the card. -->
              <template v-if="dayShown">
                <section class="rc-day" data-testid="room-card-day">
                  <p class="rc-day-eyebrow">{{ day.eyebrow }}</p>
                  <DayGlyph :kind="day.glyph || 'fullmoon'" :at="Number.isInteger(day.at) ? day.at : -1" :still="reduced" />
                  <h2 ref="dayNameEl" class="rc-name rc-day-name" tabindex="-1">{{ day.name }}</h2>
                  <p v-if="day.say" class="rc-day-say">{{ day.say }}</p>
                  <p v-if="day.when" class="rc-day-when">{{ day.when }}</p>
                  <div class="rc-rule" aria-hidden="true">◆</div>
                  <dl v-if="day.rows && day.rows.length" class="rc-day-rows">
                    <div v-for="r in day.rows" :key="r.label">
                      <dt>{{ r.label }}</dt>
                      <dd>{{ r.text }}</dd>
                    </div>
                  </dl>
                  <p v-if="day.note" class="rc-day-note">{{ day.note }}</p>
                </section>
                <!-- Two marks on one day (2026-10-31, "We can have both"): each
                     other mark below the main one, in the same style, after a
                     thin divider. -->
                <template v-for="m in dayAlso" :key="m.key">
                  <hr class="rc-day-divider" aria-hidden="true">
                  <section class="rc-day" data-testid="room-card-day-also">
                    <p class="rc-day-eyebrow">{{ m.eyebrow }}</p>
                    <DayGlyph :kind="m.glyph || 'fullmoon'" :at="Number.isInteger(m.at) ? m.at : -1" :still="reduced" />
                    <h2 class="rc-name rc-day-name">{{ m.name }}</h2>
                    <p v-if="m.say" class="rc-day-say">{{ m.say }}</p>
                    <p v-if="m.when" class="rc-day-when">{{ m.when }}</p>
                    <div class="rc-rule" aria-hidden="true">◆</div>
                    <dl v-if="m.rows && m.rows.length" class="rc-day-rows">
                      <div v-for="r in m.rows" :key="r.label">
                        <dt>{{ r.label }}</dt>
                        <dd>{{ r.text }}</dd>
                      </div>
                    </dl>
                    <p v-if="m.note" class="rc-day-note">{{ m.note }}</p>
                  </section>
                </template>
              </template>
              <template v-else-if="view === 'summary'">
                <p class="rc-orn" aria-hidden="true">✦ ☾ ✦</p>
                <h2 ref="nameEl" class="rc-name" tabindex="-1">{{ name }}</h2>
                <p v-if="description" class="rc-desc" data-testid="room-card-description">{{ description }}</p>
                <div class="rc-rule" aria-hidden="true">◆</div>
                <dl v-if="rows.length" class="rc-rows">
                  <template v-for="r in rows" :key="r.label">
                    <dt>{{ r.label }}</dt>
                    <dd>
                      <a v-if="r.href" :href="r.href" target="_blank" rel="noopener noreferrer">{{ r.value }}</a>
                      <template v-else>{{ r.value }}</template>
                    </dd>
                  </template>
                </dl>
                <div v-if="signs.length" class="rc-signs">
                  <span v-for="s in signs" :key="s.role" :title="s.role">
                    <img v-if="s.img" :src="s.img" alt="" class="rc-sign-img" />
                    <b v-else aria-hidden="true">{{ s.glyph }}</b>{{ s.value }}<span class="rc-sr"> {{ s.role }}</span>
                  </span>
                </div>
                <!-- The old About page's tabs, opened here in place (Chris 2026-10-30). -->
                <div class="rc-links">
                  <button ref="deckRow" type="button" class="rc-link" data-testid="room-card-deck" @click.stop="openPanel('deck')">Card Deck ›</button>
                  <button ref="sharedRow" type="button" class="rc-link" data-testid="room-card-lessons" @click.stop="openPanel('shared')">Lessons ›</button>
                  <button v-if="hasDrop" ref="dropRow" type="button" class="rc-link" data-testid="room-card-drop" @click.stop="openPanel('drop')">Today's drop ›</button>
                </div>
                <div v-if="data && data.sub_domain" class="rc-quiet">
                  <button
                    type="button"
                    class="rc-follow" :class="{ following: data.is_following || data.is_home }"
                    data-testid="rail-follow"
                    @click.stop="data.is_home ? null : $emit('toggle-follow')"
                  >{{ (data.is_following || data.is_home) ? '★ In Your Mystics' : '☆ Add to Your Mystics' }}</button>
                  <span v-if="data.is_home" class="rc-home" data-testid="rail-home">Your home Mystic</span>
                  <button v-else type="button" class="rc-make-home" data-testid="rail-make-home" @click.stop="$emit('make-home')">Make this my home Mystic</button>
                </div>
              </template>
              <section v-else class="rc-panel" data-testid="room-card-panel" :aria-label="panelTitle">
                <h3 ref="panelTitleEl" class="rc-panel-title" tabindex="-1">{{ panelTitle }}</h3>
                <ReaderInfoTabs
                  bare
                  :data="data"
                  :initial-tab="view"
                  @open-lesson="$emit('open-lesson', $event)"
                />
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import ReaderInfoTabs from '@/components/ui/ReaderInfoTabs.vue'
import DayGlyph from '@/components/ui/DayGlyph.vue'
import { store } from '@/store'

const props = defineProps({
  name: { type: String, default: '' },
  image: { type: String, default: '' },
  initial: { type: String, default: '' },
  front: { type: Object, required: true },
  back: { type: Object, required: true },
  flipped: { type: Boolean, default: false },
  // The Mystic's full description (the old About page's), else the one line.
  description: { type: String, default: '' },
  rows: { type: Array, default: () => [] },
  signs: { type: Array, default: () => [] },
  // The reader detail: the deck, the lessons, the drop, Follow and home.
  data: { type: Object, default: null },
  // A view an entry asked for: 'summary' | 'deck' | 'shared' | 'drop'.
  view: { type: String, default: '' },
  // 'arrive' plays the light coming up once started; 'settled' / 'none' are simply there.
  scene: { type: String, default: 'settled' },
  started: { type: Boolean, default: true },
  // The one-time tilt and glint (first visit on this device).
  cue: { type: Boolean, default: false },
  wide: { type: Boolean, default: false },
  reduced: { type: Boolean, default: false },
  // Which face the back shows: the About, or the day (the sky line turned it).
  face: { type: String, default: 'about' },
  // The marked day's card (the server's greeting.day), or null.
  day: { type: Object, default: null },
})
const emit = defineEmits(['toggle', 'unflip', 'image-ready', 'open-lesson', 'toggle-follow', 'make-home'])

const frontEl = ref(null)
const nameEl = ref(null)
const innerEl = ref(null)
const panelTitleEl = ref(null)
const deckRow = ref(null)
const sharedRow = ref(null)
const dropRow = ref(null)
const dayNameEl = ref(null)
const turning = ref(false)
const dayShown = computed(() => props.face === 'day' && !!props.day)
const dayAlso = computed(() => (props.day && Array.isArray(props.day.also) ? props.day.also : []))
let turnTimer = null

const boxStyle = computed(() => {
  const r = props.flipped ? props.back : props.front
  return { width: `${r.w}px`, height: `${r.h}px`, left: `${r.x}px`, top: `${r.y}px` }
})

// ── The back's views: the summary, or one of the old About tabs in place ──
const VIEWS = new Set(['summary', 'deck', 'shared', 'drop'])
const pick = (v) => (VIEWS.has(v) ? v : 'summary')
const view = ref(pick(props.view))
const TITLES = { deck: 'Card Deck', shared: 'Lessons', drop: 'Today\'s drop' }
const panelTitle = computed(() => TITLES[view.value] || '')
const hasDrop = computed(() => !!(props.data && (props.data.today_drop || props.data.drop_pending)))
watch(() => props.view, (v) => { if (v) view.value = pick(v) })
let summaryScroll = 0
function openPanel (v) {
  if (innerEl.value) summaryScroll = innerEl.value.scrollTop
  view.value = v
  nextTick(() => {
    if (innerEl.value) innerEl.value.scrollTop = 0
    if (panelTitleEl.value) panelTitleEl.value.focus({ preventScroll: true })
  })
}
function closePanel () {
  const from = view.value
  view.value = 'summary'
  nextTick(() => {
    if (innerEl.value) innerEl.value.scrollTop = summaryScroll
    const row = { deck: deckRow, shared: sharedRow, drop: dropRow }[from]
    const el = (row && row.value) || nameEl.value
    if (el && el.focus) el.focus({ preventScroll: true })
  })
}

// The turn: the gold edge catches the light, then focus goes to the name on
// the back (or the open panel's title), or back to the card on return. Each
// turn to the back starts at the summary, unless an entry asked for a view.
watch(() => props.flipped, (on) => {
  if (on) { view.value = pick(props.view); summaryScroll = 0; if (innerEl.value) innerEl.value.scrollTop = 0 }
  clearTimeout(turnTimer)
  turning.value = !props.reduced
  turnTimer = setTimeout(() => {
    turning.value = false
    nextTick(() => {
      // The day's card turning back: focus goes to the sky line (the room's).
      const el = on
        ? (dayShown.value ? dayNameEl.value : (view.value === 'summary' ? nameEl.value : panelTitleEl.value))
        : (dayShown.value ? null : frontEl.value)
      if (el && el.focus) el.focus({ preventScroll: true })
    })
  }, props.reduced ? 200 : 600)
})

// Esc turns the card back, from the summary or a panel; not while a picture
// or a card's detail is open over it (Esc is theirs).
function onKey (e) {
  if (e.key !== 'Escape' || !props.flipped) return
  if (store.card_detail || document.querySelector('.drop-modal')) return
  emit('unflip')
}
onMounted(() => document.addEventListener('keydown', onKey))
onBeforeUnmount(() => { document.removeEventListener('keydown', onKey); clearTimeout(turnTimer) })
</script>

<style scoped>
.room-card {
  position: absolute; z-index: 5; perspective: 1400px;
  /* The size change lives in the turn's middle (22% to 78% of 0.6s). */
  transition: width 0.336s cubic-bezier(0.45, 0, 0.55, 1) 0.132s, height 0.336s cubic-bezier(0.45, 0, 0.55, 1) 0.132s,
    left 0.336s cubic-bezier(0.45, 0, 0.55, 1) 0.132s, top 0.336s cubic-bezier(0.45, 0, 0.55, 1) 0.132s;
}
.room-card.is-turning { animation: rc-edge 0.6s ease-in-out; }
@keyframes rc-edge {
  0%, 100% { filter: none; }
  50% { filter: drop-shadow(0 0 26px rgba(255, 214, 140, 0.75)); }
}
.rc-lift, .rc-tilt { position: absolute; inset: 0; transform-style: preserve-3d; }
.rc-card {
  position: absolute; inset: 0; transform-style: preserve-3d; border-radius: 16px;
  transition: transform 0.6s cubic-bezier(0.65, 0, 0.35, 1);
}
.room-card.is-flipped .rc-card { transform: rotateY(180deg); }
.rc-face {
  position: absolute; inset: 0; border-radius: 16px; overflow: hidden;
  backface-visibility: hidden; -webkit-backface-visibility: hidden;
}
.rc-front { cursor: pointer; border: 2px solid rgba(201, 169, 78, 0.55); background: #120726; }
.rc-front:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 3px; }
.rc-img { display: block; width: 100%; height: 100%; object-fit: cover; }
.rc-initial {
  display: grid; place-items: center; width: 100%; height: 100%;
  font-family: 'Cinzel', serif; font-weight: 500; font-size: 72px; color: rgba(255, 223, 163, 0.85);
  background: radial-gradient(circle at 50% 35%, rgba(138, 95, 214, 0.35), transparent 60%), linear-gradient(180deg, #1f1140, #0f0723);
}
.rc-glint {
  position: absolute; inset: -20%; pointer-events: none; transform: translateX(-130%) rotate(12deg);
  background: linear-gradient(100deg, transparent 38%, rgba(255, 236, 190, 0) 42%, rgba(255, 236, 190, 0.55) 50%, rgba(255, 236, 190, 0) 58%, transparent 62%);
  mix-blend-mode: screen;
}
.room-card.is-turning:not(.is-flipped) .rc-glint,
.room-card.is-turning.is-flipped .rc-glint { animation: rc-glint 0.3s ease-in both; }
.rc-turnmark {
  position: absolute; right: 8px; bottom: 8px; width: 26px; height: 26px; border-radius: 50%;
  display: grid; place-items: center; font-size: 14px; line-height: 1;
  background: rgba(10, 5, 22, 0.62); border: 1px solid rgba(240, 217, 163, 0.55); color: #f0d9a3;
}
/* The built gold-to-lilac aura, alive once the light is up. */
.rc-front { animation: rc-aura 4.5s ease-in-out infinite; }
.room-card.is-arrive .rc-front { animation: rc-aura 4.5s ease-in-out 1s infinite; }
@keyframes rc-aura {
  0%, 100% { box-shadow: 0 0 30px -4px rgba(201, 169, 78, 0.45), 0 0 0 3px rgba(201, 169, 78, 0.18), 0 8px 26px rgba(0, 0, 0, 0.55); }
  50% { box-shadow: 0 0 48px 0 rgba(195, 169, 255, 0.55), 0 0 0 5px rgba(195, 169, 255, 0.22), 0 8px 26px rgba(0, 0, 0, 0.55); }
}

/* Arrival: the light comes up. Dark, grey and soft, 14px low, then itself. */
.room-card.is-arrive:not(.is-started) .rc-lift { opacity: 0; }
.room-card.is-arrive.is-started .rc-lift { animation: rc-rise 1s cubic-bezier(0.33, 1, 0.68, 1) both; }
.room-card.is-arrive.is-started .rc-img,
.room-card.is-arrive.is-started .rc-initial { animation: rc-light 1s cubic-bezier(0.33, 1, 0.68, 1) both; }
@keyframes rc-rise {
  from { opacity: 0; transform: translateY(14px) scale(0.965); }
  70% { opacity: 1; }
  to { opacity: 1; transform: none; }
}
@keyframes rc-light {
  from { filter: brightness(0.15) saturate(0.3) blur(6px); }
  to { filter: none; }
}
/* First visit on this device: one slow tilt that shows it is a card, and a glint across it. */
.room-card.is-cue.is-started .rc-tilt { animation: rc-tilt 1.5s ease-in-out 1.15s both; }
.room-card.is-cue.is-started .rc-glint { animation: rc-glint 0.9s ease-in-out 1.3s both; }
@keyframes rc-tilt {
  0%, 100% { transform: rotateY(0deg); }
  50% { transform: rotateY(-15deg); }
}
@keyframes rc-glint {
  from { transform: translateX(-130%) rotate(12deg); }
  to { transform: translateX(130%) rotate(12deg); }
}

/* The back: a tarot back, deep indigo, a gold frame with four corner stars, a faint sigil. */
.rc-back {
  transform: rotateY(180deg); color: #efe6ff;
  background:
    radial-gradient(120% 80% at 50% 0%, rgba(138, 95, 214, 0.28), transparent 60%),
    radial-gradient(90% 60% at 50% 100%, rgba(214, 160, 92, 0.12), transparent 70%),
    linear-gradient(180deg, #1b0f3a, #0d0620);
  border: 2px solid rgba(201, 169, 78, 0.65);
  box-shadow: 0 0 40px -6px rgba(201, 169, 78, 0.45), 0 10px 30px rgba(0, 0, 0, 0.6);
}
.rc-frame { position: absolute; inset: 8px; border: 1px solid rgba(240, 217, 163, 0.35); border-radius: 10px; pointer-events: none; }
.rc-frame b { position: absolute; width: 9px; height: 9px; color: #f0d9a3; font-size: 10px; line-height: 9px; text-align: center; }
.rc-frame b:nth-child(1) { top: -5px; left: -5px; }
.rc-frame b:nth-child(2) { top: -5px; right: -5px; }
.rc-frame b:nth-child(3) { bottom: -5px; left: -5px; }
.rc-frame b:nth-child(4) { bottom: -5px; right: -5px; }
.rc-sigil { position: absolute; left: 50%; top: 50%; width: 78%; aspect-ratio: 1; transform: translate(-50%, -50%); opacity: 0.07; pointer-events: none; }
.rc-inner {
  position: absolute; inset: 14px; overflow-x: hidden; overflow-y: auto; overscroll-behavior: contain; padding: 18px 16px 14px;
  display: flex; flex-direction: column; align-items: center; text-align: center;
  scrollbar-width: thin; scrollbar-color: rgba(240, 217, 163, 0.28) transparent;
  opacity: 0; transition: opacity 0.15s ease;
}
.rc-inner::-webkit-scrollbar { width: 4px; }
.rc-inner::-webkit-scrollbar-thumb { background: rgba(240, 217, 163, 0.28); border-radius: 2px; }
.rc-inner.is-panel { align-items: stretch; text-align: left; padding-top: 6px; }
/* The back's words come up over the last 45% of the turn. */
.room-card.is-flipped .rc-inner { opacity: 1; transition: opacity 0.27s ease 0.33s; }
.rc-back-x {
  position: absolute; z-index: 3; top: 14px; right: 14px; width: 28px; height: 28px; padding: 0; border-radius: 50%;
  display: grid; place-items: center; cursor: pointer;
  background: rgba(10, 5, 22, 0.5); border: 1px solid rgba(240, 217, 163, 0.45); color: #f0d9a3;
}
.rc-panel-back { right: auto; left: 14px; }
.rc-day-name:focus-visible { outline: none; }
.rc-back-x:focus-visible, .rc-link:focus-visible, .rc-follow:focus-visible, .rc-make-home:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 3px; }
.rc-orn { color: #f0d9a3; font-size: 11px; letter-spacing: 0.5em; opacity: 0.85; margin: 0 0 8px; padding-left: 0.5em; }
.rc-name {
  font-family: 'Cinzel', serif; font-weight: 600; font-size: 23px; letter-spacing: 0.04em; color: #f6e7c1; margin: 0;
  text-shadow: 0 0 18px rgba(201, 169, 78, 0.35); outline: none;
}
/* The Mystic's own words, whole: upright, a paragraph per line break. */
.rc-desc {
  font-family: 'Cormorant Garamond', Georgia, serif; font-size: 17px; line-height: 1.42; white-space: pre-line;
  color: #efe6ff; margin: 10px 0 0; max-width: 24em; text-align: left;
}
.rc-rule { display: flex; align-items: center; gap: 8px; width: 70%; margin: 14px auto 12px; color: #f0d9a3; font-size: 9px; }
.rc-rule::before, .rc-rule::after { content: ''; flex: 1; height: 1px; background: linear-gradient(90deg, transparent, rgba(240, 217, 163, 0.6), transparent); }
.rc-rows { display: grid; grid-template-columns: auto 1fr; gap: 8px 12px; margin: 0; text-align: left; width: 100%; }
.rc-rows dt {
  font-family: 'Cinzel', serif; font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase;
  color: rgba(240, 217, 163, 0.78); padding-top: 4px; white-space: nowrap;
}
.rc-rows dd { margin: 0; min-width: 0; overflow-wrap: anywhere; font-family: 'Cormorant Garamond', Georgia, serif; font-size: 16.5px; line-height: 1.3; color: #f3ecff; }
.rc-rows a { color: #e6d8ff; text-decoration: underline; text-decoration-color: rgba(230, 216, 255, 0.4); text-underline-offset: 3px; }
.rc-signs { display: flex; justify-content: center; flex-wrap: wrap; gap: 6px 14px; margin: 12px 0 0; font-family: 'Cormorant Garamond', Georgia, serif; font-size: 15.5px; color: #e9dcff; }
.rc-signs > span { display: inline-flex; align-items: center; }
.rc-signs b { color: #f0d9a3; font-weight: 500; margin-right: 3px; }
.rc-sign-img { width: 18px; height: 18px; border-radius: 5px; object-fit: cover; margin-right: 5px; }
.rc-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
/* The old About tabs, as quiet rows at the foot of the summary. */
.rc-links { margin-top: auto; padding-top: 16px; width: 100%; display: flex; flex-direction: column; }
.rc-link {
  width: 100%; padding: 11px 4px; cursor: pointer; background: none; border: 0;
  border-top: 1px solid rgba(240, 217, 163, 0.18);
  font-family: 'Cinzel', serif; font-size: 13px; font-weight: 600; letter-spacing: 0.1em; color: #f0d9a3;
}
.rc-link:last-child { border-bottom: 1px solid rgba(240, 217, 163, 0.18); }
.rc-link:hover { color: #FFDFA3; background: rgba(240, 217, 163, 0.05); }
/* Follow and home: the About page's quiet row, kept. */
.rc-quiet { display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 8px 16px; margin-top: 14px; }
.rc-follow {
  padding: 6px 13px; border-radius: 999px; cursor: pointer; background: transparent;
  font: 600 12px 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #c3a9ff; border: 1px solid rgba(195, 169, 255, 0.35);
}
.rc-follow:hover { border-color: rgba(195, 169, 255, 0.7); color: #e6d8ff; }
.rc-follow.following { color: #8be0a0; border-color: rgba(63, 174, 90, 0.45); }
.rc-make-home { padding: 4px 0; background: none; border: 0; cursor: pointer; color: #c3a9ff;
  font: 600 12.5px 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; text-decoration: underline; text-underline-offset: 3px; }
.rc-home { color: #e7c983; font: 600 12.5px 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
/* A tab opened in place: its title level with the back arrow and the ✕. */
.rc-panel { width: 100%; }
.rc-panel-title {
  margin: 0 36px 14px; min-height: 28px; display: flex; align-items: center; justify-content: center; outline: none;
  font-family: 'Cinzel', serif; font-weight: 600; font-size: 17px; letter-spacing: 0.06em; color: #f6e7c1;
}
/* The day's face: the About's type, the rows echo READS FOR / VOICE. */
.rc-day { width: 100%; min-height: 100%; display: flex; flex-direction: column; align-items: center; text-align: center; }
.rc-day-eyebrow {
  margin: 0; font-family: 'Cinzel', serif; font-size: 10.5px; font-weight: 600; letter-spacing: 0.24em;
  text-transform: uppercase; color: rgba(240, 217, 163, 0.82); padding-left: 0.24em;
}
.rc-day-name { font-size: 25px; line-height: 1.15; }
.rc-day-say { margin: 6px 0 0; font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 16px; line-height: 1.3; color: #d9cdf3; }
.rc-day-when {
  margin: 6px 0 0; font-family: 'Cinzel', serif; font-size: 10.5px; font-weight: 600; letter-spacing: 0.12em;
  text-transform: uppercase; color: rgba(240, 217, 163, 0.7);
}
.rc-day-rows { margin: 0; width: 100%; text-align: left; display: grid; gap: 8px; }
.rc-day-rows dt {
  font-family: 'Cinzel', serif; font-size: 10px; font-weight: 600; letter-spacing: 0.16em;
  text-transform: uppercase; color: rgba(240, 217, 163, 0.78);
}
.rc-day-rows dd { margin: 2px 0 0; font-family: 'Cormorant Garamond', Georgia, serif; font-size: 16.5px; line-height: 1.32; color: #f3ecff; }
.rc-day-note {
  margin: auto 0 0; padding-top: 12px; font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic;
  font-size: 14.5px; line-height: 1.3; color: #b9a9dc;
}
/* Two marks: each section only as tall as its words; a thin gold divider between. */
.rc-inner.is-day-both .rc-day { min-height: 0; flex: 0 0 auto; }
.rc-day-divider { width: 60%; margin: 18px auto 14px; border: 0; border-top: 1px solid rgba(240, 217, 163, 0.32); flex: 0 0 auto; }
.room-card.is-wide .rc-day-name { font-size: 30px; }
.room-card.is-wide .rc-day-rows dd { font-size: 18px; }
.room-card.is-wide .rc-day-rows { gap: 12px; }
.room-card.is-wide .rc-day-say { font-size: 17.5px; }
.room-card.is-wide .rc-day-when { font-size: 11px; }
.room-card.is-wide :deep(.rc-day-glyph) { width: 62px; height: 62px; }
.room-card.is-wide .rc-name { font-size: 27px; }
.room-card.is-wide .rc-desc { font-size: 18.5px; }
.room-card.is-wide .rc-rows dd { font-size: 18px; }
.room-card.is-wide .rc-rows { gap: 10px 16px; }
.room-card.is-wide .rc-inner { padding: 26px 26px 18px; }

/* Reduced motion: everything simply there; the turn is a 0.2s crossfade. */
.room-card.is-reduced { transition: none; }
.room-card.is-reduced .rc-card { transition: none; transform: none !important; }
.room-card.is-reduced .rc-face { backface-visibility: visible; -webkit-backface-visibility: visible; transition: opacity 0.2s ease; }
.room-card.is-reduced .rc-back { transform: none; opacity: 0; visibility: hidden; }
.room-card.is-reduced.is-flipped .rc-back { opacity: 1; visibility: visible; }
.room-card.is-reduced.is-flipped .rc-front { opacity: 0; visibility: hidden; }
.room-card.is-reduced .rc-inner { opacity: 1; transition: none; }
.room-card.is-reduced .rc-lift, .room-card.is-reduced .rc-tilt, .room-card.is-reduced .rc-img,
.room-card.is-reduced .rc-initial, .room-card.is-reduced .rc-glint, .room-card.is-reduced .rc-front { animation: none !important; opacity: 1; }
.room-card.is-reduced.is-flipped .rc-front { opacity: 0; }
</style>
